import { Connection, PublicKey, Transaction } from '@solana/web3.js';

// Normalize base URL by removing trailing slash to avoid double slashes
export const getBaseUrl = (url: string): string => {
  return url.replace(/\/+$/, '');
};

const API_BASE_URL = getBaseUrl(process.env.REACT_APP_API_URL || 'http://localhost:3001');

export interface BlinkMetadata {
  '@context': string;
  '@type': string;
  name: string;
  description: string;
  image: string;
  url: string;
  potentialAction?: Array<{
    '@type': string;
    name: string;
    target: {
      '@type': string;
      urlTemplate: string;
      actionPlatform: string[];
    };
  }>;
}

export interface PlayResult {
  transaction: string;
  image: string; // Absolute HTTP/HTTPS URL (not base64)
  result: {
    success: boolean;
    amount?: number;
    nearMiss?: number;
  };
  state: any;
}

export class BlinksApi {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    // Normalize baseUrl by removing trailing slashes to avoid double slashes
    this.baseUrl = getBaseUrl(baseUrl);
  }

  /**
   * Get Blink metadata for a crate
   */
  async getBlinkMetadata(crateId: string, account?: string): Promise<BlinkMetadata> {
    const url = new URL(`${this.baseUrl}/api/blinks/action/${crateId}`);
    if (account) {
      url.searchParams.append('account', account);
    }

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`Failed to fetch Blink metadata: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Play a crate (open it)
   */
  async playCrate(
    crateId: string,
    amount: number,
    account: string
  ): Promise<PlayResult> {
    const url = new URL(`${this.baseUrl}/api/blinks/action/${crateId}/play`);
    url.searchParams.append('amount', amount.toString());
    url.searchParams.append('account', account);

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      let errorMessage = response.statusText;
      try {
        const errorData = await response.json();
        if (errorData.error?.message) {
          errorMessage = errorData.error.message;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // Ignore JSON parse error
      }
      throw new Error(`Failed to play crate: ${errorMessage}`);
    }

    return response.json();
  }

  /**
   * Create a new crate and return the Blink URL
   * @deprecated Use createBlink for Season 1
   */
  async createCrate(
    name: string,
    creatorWallet: string
  ): Promise<{ crateId: string; blinkUrl: string }> {
    // Generate a unique crate ID
    const crateId = `crate_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    
    // The Blink URL that will be shared
    const blinkUrl = `${this.baseUrl}/api/blinks/action/${crateId}`;

    // Initialize the crate by fetching its metadata (which will create it)
    const initUrl = new URL(`${this.baseUrl}/api/blinks/action/${crateId}`);
    initUrl.searchParams.append('name', name);
    initUrl.searchParams.append('creator', creatorWallet);

    const response = await fetch(initUrl.toString());
    if (!response.ok) {
      throw new Error(`Failed to create crate: ${response.statusText}`);
    }

    return { crateId, blinkUrl };
  }

  /**
   * Create a Blink link for Season 1 (uses fixed crate with affiliate address)
   */
  async createBlink(
    affiliateAddress: string
  ): Promise<{ blinkUrl: string }> {
    // Season 1: Fixed crate PDA (only one crate exists)
    const SEASON_1_CRATE_PDA = '7Exz5fq3B9anPpLGNJqvMN7qQEoTDxL3X4jpX36YaRGm';
    
    // Validate affiliate address
    try {
      new PublicKey(affiliateAddress);
    } catch {
      throw new Error('Invalid affiliate wallet address');
    }
    
    // Build Blink URL with affiliate parameter
    const blinkUrl = `${this.baseUrl}/api/blinks/action/${SEASON_1_CRATE_PDA}?affiliate=${affiliateAddress}`;

    return { blinkUrl };
  }

  /**
   * Get crate info/probability
   */
  async getCrateInfo(crateId: string, account?: string): Promise<BlinkMetadata> {
    const url = new URL(`${this.baseUrl}/api/blinks/action/${crateId}/info`);
    if (account) {
      url.searchParams.append('account', account);
    }

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`Failed to fetch crate info: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Verify if a case exists on-chain by attempting to fetch its metadata
   */
  async verifyCaseExists(caseId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/blinks/action/${caseId}`);
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Get crate configuration data from backend
   */
  async getCrateConfig(caseId: string): Promise<{
    name?: string;
    entryCost: number;
    totalPlays: number;
    totalEarnings: number;
    hasJackpotTier?: boolean;
    jackpotBase?: number;
    jackpotBalance?: number;
    tiers?: Array<{
      name: string;
      chance: number;
      multiplier: number;
      color: string;
    }>;
  } | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/cases/config/${caseId}`);
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * Sign and send a transaction
   */
  async signAndSendTransaction(
    transactionBase64: string,
    connection: Connection,
    signTransaction: (tx: Transaction) => Promise<Transaction>
  ): Promise<string> {
    const transactionBuffer = Buffer.from(transactionBase64, 'base64');
    const transaction = Transaction.from(transactionBuffer);

    // Sign the transaction
    const signedTransaction = await signTransaction(transaction);

    // Send the transaction
    const signature = await connection.sendRawTransaction(
      signedTransaction.serialize(),
      {
        skipPreflight: false,
        maxRetries: 3
      }
    );

    // Wait for confirmation
    await connection.confirmTransaction(signature, 'confirmed');

    return signature;
  }

  /**
   * Create a new case (Season 2)
   */
  async createCase(data: {
    name: string;
    dropTable: Array<{ chanceBps: number; tokenAmount: number }>;
    jackpotBase?: number;
    hasJackpotTier?: boolean;
    jackpotTierIndex?: number;
    creatorWallet: string;
  }): Promise<{
    transaction: string;
    casePda: string;
    entryCost: number;
    averagePayout: number;
    rtp: string;
    message: string;
  }> {
    const response = await fetch(`${this.baseUrl}/api/cases/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to create case');
    }

    return response.json();
  }

  /**
   * Confirm case creation after transaction is confirmed on-chain
   */
  async confirmCase(casePda: string, transactionSignature: string): Promise<{
    success: boolean;
    message: string;
    casePda: string;
  }> {
    const response = await fetch(`${this.baseUrl}/api/cases/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        casePda,
        transactionSignature,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to confirm case');
    }

    return response.json();
  }

  /**
   * Build a ClosePlay transaction to reclaim rent from a settled play
   */
  async closePlay(
    playId: string,
    playerWallet: string
  ): Promise<{
    transaction: string;
    message: string;
  }> {
    const response = await fetch(`${this.baseUrl}/api/blinks/close-play`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        playId,
        playerWallet,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to build close-play transaction');
    }

    return response.json();
  }

  /**
   * Get play history for a wallet
   */
  async getPlayHistory(wallet: string): Promise<{
    plays: Array<{
      playId: string;
      crateId: string;
      crateName?: string;
      amount: number;
      isWinner: boolean;
      winAmount?: number;
      isClosed: boolean;
      transactionSignature?: string;
      resolvedAt: Date;
      createdAt: Date;
    }>;
  }> {
    const response = await fetch(`${this.baseUrl}/api/blinks/plays/history/${wallet}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to fetch play history');
    }

    return response.json();
  }

  /**
   * Build a batch transaction to close multiple plays at once
   */
  async closePlaysBatch(
    playerWallet: string,
    playIds: string[]
  ): Promise<{
    transaction: string;
    message: string;
    playIds: string[];
    count: number;
  }> {
    const response = await fetch(`${this.baseUrl}/api/blinks/close-plays-batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        playerWallet,
        playIds,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to build batch close transaction');
    }

    return response.json();
  }

  /**
   * Get cases created by a specific wallet
   */
  async getCasesByCreator(wallet: string): Promise<{
    cases: Array<{
      crateId: string;
      name: string;
      creatorWallet: string;
      entryCost: number;
      totalPlays: number;
      totalEarnings: number;
      hasJackpotTier: boolean;
      jackpotBase?: number;
      jackpotTierIndex?: number;
      createdAt: Date;
      status: string;
    }>;
  }> {
    const response = await fetch(`${this.baseUrl}/api/cases/by-creator/${wallet}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to fetch cases');
    }

    return response.json();
  }

  /**
   * Get all cases from database
   */
  async getAllCases(): Promise<{
    cases: Array<{
      crateId: string;
      name: string;
      creatorWallet: string;
      entryCost: number;
      totalPlays: number;
      totalEarnings: number;
      hasJackpotTier: boolean;
      jackpotBase?: number;
      jackpotBalance?: number;
    }>;
  }> {
    const response = await fetch(`${this.baseUrl}/api/cases/all`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to fetch all cases');
    }

    return response.json();
  }

  /**
   * Update database after close-play transaction is confirmed
   */
  async closePlayResult(
    playId: string,
    signature: string
  ): Promise<{
    success: boolean;
    message: string;
    playId: string;
    signature: string;
  }> {
    const response = await fetch(`${this.baseUrl}/api/blinks/close-play/result`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        playId,
        signature,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to update close-play result');
    }

    return response.json();
  }

  /**
   * Update database after batch close-plays transaction is confirmed
   */
  async closePlaysBatchResult(
    playIds: string[],
    signature: string
  ): Promise<{
    success: boolean;
    message: string;
    playIds: string[];
    count: number;
    signature: string;
  }> {
    const response = await fetch(`${this.baseUrl}/api/blinks/close-plays-batch/result`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        playIds,
        signature,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to update batch close-plays result');
    }

    return response.json();
  }

  /**
   * Get rewards (creator and affiliate earnings) for a wallet
   */
  async getRewards(wallet: string): Promise<{
    wallet: string;
    creator: {
      totalEarnings: number;
      cases: Array<{
        crateId: string;
        crateName: string;
        totalVolume: number;
        creatorEarnings: number;
        totalPlays: number;
        entryCost: number;
        createdAt: Date;
      }>;
    };
    affiliate: {
      totalEarnings: number;
      cases: Array<{
        crateId: string;
        crateName: string;
        totalEarnings: number;
        totalPlays: number;
        firstPlayAt: Date;
        lastPlayAt: Date;
      }>;
    };
    totalEarnings: number;
  }> {
    const response = await fetch(`${this.baseUrl}/api/cases/rewards/${wallet}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to fetch rewards');
    }

    return response.json();
  }
}

export const blinksApi = new BlinksApi();

